import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { startLoadingHorariosExtraLogsProfesional, startDeletingHorariosExtraLogsProfesional } from '../../../../actions/horariosExtraLogs';
import { startLoadingVacacionesLogsProfesional, startDeletingVacacionesLogsProfesional } from '../../../../actions/vacacionesLogs';
import { TimeIcon, TrashIcon } from '../../../../assets/svg/iconos';

const timezone = process.env.REACT_APP_TIMEZONE;
const AUTO_RELOAD_INTERVAL = 5000; // 5 segundos

const LogItemProfesional = React.memo(({ log, formatFecha, formatUsuario, getAccionLabel, getAccionColor, colores }) => (
    <div style={{ 
        marginTop: '12px',
        padding: '12px',
        backgroundColor: 'white',
        border: `1px solid ${colores.color6}`,
        borderRadius: '4px'
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span 
                style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    backgroundColor: getAccionColor(log.accion),
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 'bold'
                }}
            >
                {getAccionLabel(log.accion)}
            </span>
            <span style={{ color: '#666', fontSize: '12px' }}>
                {formatFecha(log.fecha)}
            </span>
        </div>
        
        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
            <strong>Usuario:</strong> {formatUsuario(log.usuario)}
        </div>
        
        {log.razon && (
            <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#666', marginTop: '4px' }}>
                <strong>Razón:</strong> {log.razon}
            </div>
        )}
    </div>
));

export const HorariosExtraLogsSection = ({ profesionalId, empresa }) => {
    const dispatch = useDispatch();
    const { colores } = useSelector(state => state.ui);
    const { role } = useSelector(state => state.auth);
    const { logsProfesional: logsHorariosExtra, pagination: paginationHorariosExtra } = useSelector(state => state.horariosExtraLogs);
    const { logsProfesional: logsVacaciones, pagination: paginationVacaciones } = useSelector(state => state.vacacionesLogs);
    
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [limit] = useState(50);
    const [skip, setSkip] = useState(0);
    const reloadIntervalRef = useRef(null);
    
    const canViewLogs = useMemo(() => role === 'SUPERADMIN_ROLE', [role]);
    
    const reloadLogs = useCallback(() => {
        if (expanded && profesionalId && empresa && canViewLogs && skip === 0 && !loading) {
            Promise.all([
                dispatch(startLoadingHorariosExtraLogsProfesional(profesionalId, empresa, limit, 0)),
                dispatch(startLoadingVacacionesLogsProfesional(profesionalId, empresa, limit, 0))
            ]).catch((error) => {
                console.error('Error al recargar logs:', error);
            });
        }
    }, [expanded, profesionalId, empresa, canViewLogs, skip, limit, dispatch, loading]);
    
    const allItems = useMemo(() => {
        const items = [];
        
        if (logsHorariosExtra) {
            logsHorariosExtra.forEach(he => {
                const hasModifications = he.modificationHistory && he.modificationHistory.some(log => 
                    log.accion === 'modificado' || log.accion === 'eliminado'
                );
                
                if (hasModifications) {
                    items.push({
                        _id: he._id,
                        tipo: 'horario_extra',
                        tipoLabel: 'Horario Extra',
                        fechaInicio: he.fechaInicio,
                        fechaFin: he.fechaFin,
                        modificationHistory: he.modificationHistory || []
                    });
                }
            });
        }
        
        if (logsVacaciones) {
            logsVacaciones.forEach(v => {
                const hasModifications = v.modificationHistory && v.modificationHistory.some(log => 
                    log.accion === 'modificado' || log.accion === 'eliminado'
                );
                
                if (hasModifications) {
                    const tipoLabel = v.tipo === 'vacaciones' ? 'Vacaciones' : 
                                    v.tipo === 'ausencia' ? 'Ausencia' : 
                                    'Vacación';
                    items.push({
                        _id: v._id,
                        tipo: v.tipo || 'vacacion',
                        tipoLabel: tipoLabel,
                        fechaInicio: v.fechaInicio,
                        fechaFin: v.fechaFin,
                        modificationHistory: v.modificationHistory || []
                    });
                }
            });
        }
        
        items.sort((a, b) => {
            const getLatestLogDate = (item) => {
                if (!item.modificationHistory || item.modificationHistory.length === 0) {
                    return 0;
                }
                const latestLog = item.modificationHistory[0];
                return latestLog && latestLog.fecha ? new Date(latestLog.fecha).getTime() : 0;
            };
            
            const dateA = getLatestLogDate(a);
            const dateB = getLatestLogDate(b);
            
            return dateB - dateA;
        });
        
        return items;
    }, [logsHorariosExtra, logsVacaciones]);
    
    const totalLogs = useMemo(() => {
        let total = 0;
        allItems.forEach(item => {
            total += (item.modificationHistory || []).length;
        });
        return total;
    }, [allItems]);
    
    useEffect(() => {
        if (expanded && profesionalId && empresa && canViewLogs && skip === 0) {
            setLoading(true);
            Promise.all([
                dispatch(startLoadingHorariosExtraLogsProfesional(profesionalId, empresa, limit, 0)),
                dispatch(startLoadingVacacionesLogsProfesional(profesionalId, empresa, limit, 0))
            ]).catch((error) => {
                console.error('Error al cargar logs:', error);
            }).finally(() => setLoading(false));
        }
    }, [expanded, profesionalId, empresa, canViewLogs, dispatch, limit, skip]);
    
    useEffect(() => {
        if (expanded && profesionalId && empresa && canViewLogs && skip === 0) {
            reloadIntervalRef.current = setInterval(() => {
                reloadLogs();
            }, AUTO_RELOAD_INTERVAL);
            
            return () => {
                if (reloadIntervalRef.current) {
                    clearInterval(reloadIntervalRef.current);
                    reloadIntervalRef.current = null;
                }
            };
        } else {
            if (reloadIntervalRef.current) {
                clearInterval(reloadIntervalRef.current);
                reloadIntervalRef.current = null;
            }
        }
    }, [expanded, profesionalId, empresa, canViewLogs, skip, reloadLogs]);
    
    const formatFecha = useCallback((fecha) => {
        if (!fecha) return '-';
        return moment.tz(fecha, timezone).format('DD/MM/YYYY HH:mm:ss');
    }, []);
    
    const formatUsuario = useCallback((usuario) => {
        if (!usuario) return 'Usuario desconocido';
        if (typeof usuario === 'string') return usuario;
        const nombre = usuario.nombre || '';
        const apellidos = usuario.apellidos || '';
        const email = usuario.email || '';
        if (nombre || apellidos) {
            return `${nombre} ${apellidos}`.trim() + (email ? ` (${email})` : '');
        }
        return email || 'Usuario desconocido';
    }, []);
    
    const getAccionLabel = useCallback((accion) => {
        switch (accion) {
            case 'creado': return 'Creado';
            case 'modificado': return 'Modificado';
            case 'eliminado': return 'Eliminado';
            default: return accion;
        }
    }, []);
    
    const getAccionColor = useCallback((accion) => {
        switch (accion) {
            case 'creado': return '#4CAF50';
            case 'modificado': return '#1A28D7';
            case 'eliminado': return '#F44336';
            default: return colores.colorPrincipal;
        }
    }, [colores.colorPrincipal]);

    const handleDeleteLogs = useCallback(async () => {
        const confirm = window.confirm('¿Está seguro de que desea eliminar todos los logs de auditoría de todos los horarios especiales (horarios extra, vacaciones y ausencias) de este profesional? Esta acción no se puede deshacer.');
        if (!confirm) return;

        setDeleting(true);
        try {
            await Promise.all([
                dispatch(startDeletingHorariosExtraLogsProfesional(profesionalId, empresa)),
                dispatch(startDeletingVacacionesLogsProfesional(profesionalId, empresa))
            ]);
        } finally {
            setDeleting(false);
        }
    }, [dispatch, profesionalId, empresa]);

    const toggleExpanded = useCallback(() => {
        setExpanded(prev => {
            if (!prev) {
                setSkip(0);
            }
            return !prev;
        });
    }, []);

    const handleLoadMore = useCallback(() => {
        const newSkip = skip + limit;
        setSkip(newSkip);
        setLoading(true);
        Promise.all([
            dispatch(startLoadingHorariosExtraLogsProfesional(profesionalId, empresa, limit, newSkip)),
            dispatch(startLoadingVacacionesLogsProfesional(profesionalId, empresa, limit, newSkip))
        ]).catch((error) => {
            console.error('Error al cargar más logs:', error);
        }).finally(() => setLoading(false));
    }, [skip, limit, dispatch, profesionalId, empresa]);

    const hasMore = useMemo(() => {
        const hasMoreHorarios = paginationHorariosExtra && paginationHorariosExtra.hasMore;
        const hasMoreVacaciones = paginationVacaciones && paginationVacaciones.hasMore;
        return hasMoreHorarios || hasMoreVacaciones;
    }, [paginationHorariosExtra, paginationVacaciones]);
    
    if (!canViewLogs) {
        return null;
    }
    
    return (
        <div style={{ 
            marginTop: '24px', 
            border: `1px solid ${colores.color6}`, 
            overflow: 'hidden'
        }}>
            <div 
                style={{ 
                    padding: '16px', 
                    backgroundColor: colores.color6,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
                onClick={toggleExpanded}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <TimeIcon color={colores.colorPrincipal} />
                    <span style={{ color: colores.colorPrincipal, fontWeight: 'bold' }}>
                        Historial completo de cambios
                    </span>
                    {totalLogs > 0 && (
                        <span style={{ 
                            backgroundColor: colores.colorPrincipal, 
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                        }}>
                            {totalLogs}
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {expanded && totalLogs > 0 && (
                        <div 
                            style={{ 
                                cursor: deleting ? 'not-allowed' : 'pointer',
                                opacity: deleting ? 0.6 : 1,
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                            onClick={deleting ? undefined : handleDeleteLogs}
                            title="Eliminar todos los logs"
                        >
                            <TrashIcon color={deleting ? '#999' : colores.colorPrincipal} />
                            <span style={{ fontSize: '14px', color: deleting ? '#999' : colores.colorPrincipal }}>
                                {deleting ? 'Eliminando...' : 'Eliminar logs'}
                            </span>
                        </div>
                    )}
                    <span style={{ color: colores.colorPrincipal, fontSize: '20px' }}>
                        {expanded ? '−' : '+'}
                    </span>
                </div>
            </div>
            
            {expanded && (
                <div style={{ padding: '16px', maxHeight: '500px', overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            Cargando historial...
                        </div>
                    ) : allItems && allItems.length > 0 ? (
                        <div>
                            {allItems.map((item, idx) => (
                                <div key={item._id || idx} style={{ 
                                    marginBottom: '16px', 
                                    padding: '12px', 
                                    border: `1px solid ${colores.color6}`,
                                    backgroundColor: '#FAFAFA'
                                }}>
                                    <div style={{ borderBottom: `1px solid ${colores.color6}` }}>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                            {item.tipoLabel}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {formatFecha(item.fechaInicio)} - {formatFecha(item.fechaFin)}
                                        </div>
                                    </div>
                                    
                                    {item.modificationHistory && item.modificationHistory.length > 0 ? (
                                        <div>
                                            {item.modificationHistory.map((log, logIdx) => (
                                                <LogItemProfesional
                                                    key={`${item._id}-${logIdx}-${log.fecha}`}
                                                    log={log}
                                                    formatFecha={formatFecha}
                                                    formatUsuario={formatUsuario}
                                                    getAccionLabel={getAccionLabel}
                                                    getAccionColor={getAccionColor}
                                                    colores={colores}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                                            Sin cambios registrados
                                        </div>
                                    )}
                                </div>
                            ))}
                            
                            {hasMore && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <div 
                                        className="btn btn-cancel"
                                        style={{ 
                                            backgroundColor: colores.colorPrincipal, 
                                            color: 'white',
                                            display: 'inline-block',
                                            cursor: loading ? 'not-allowed' : 'pointer',
                                            opacity: loading ? 0.6 : 1
                                        }}
                                        onClick={loading ? undefined : handleLoadMore}
                                    >
                                        {loading ? 'Cargando...' : 'Cargar más'}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            No hay historial de cambios disponible.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

